import React from 'react';
import * as d3 from 'd3';
import Tooltip from './Tooltip';
import { FONT_BLACK, SUCCESS_GREEN } from '../../../../../colors';

const MARGIN = {
  TOP: 10,
  RIGHT: 40,
  LEFT: 0,
  BOTTOM: 0,
};

const PADDING = 0;
const BAR_HEIGHT = 20;
const BAR_SPACING = 15;

const LABEL_FONT_SIZE = 14;
const LABEL_LINE_HEIGHT = 16;
const LABEL_GAP = 2;

/** Converts value to percentage (0–100) without mutating the array */
const toPercentages = (arr, decimals = 1) => {
  const grand = arr.reduce((s, { current = 0, forecast = 0 }) => s + Number(current || 0) + Number(forecast || 0), 0);
  if (!grand) {
    return arr.map(i => ({ ...i, current: 0, forecast: 0 }));
  }
  return arr.map(i => ({
    ...i,
    current: Number(((Number(i.current || 0) / grand) * 100).toFixed(decimals)),
    forecast: Number(((Number(i.forecast || 0) / grand) * 100).toFixed(decimals)),
  }));
};

/**
 * PerformanceBreakdownPlot renders a vertical list of horizontal bars with each bar's
 * name label placed **below** the bar to maximize usable width. Values can be shown
 * as raw numbers or auto-normalized percentages.
 *
 * Data shape:
 *   [{ name: string, value: number }, ...]
 *
 * Layout/details:
 * - The component computes its own total height based on rows (bar + label + spacing).
 * - Vertical gridlines are drawn at linear x-scale ticks.
 * - Bars can be filled as solid color or with a diagonal hatch pattern.
 *
 * @param {Object[]} data - Series to plot. Each item must include `name` and `value`.
 * @param {number} width - SVG width in pixels.
 * @param {number} height - (Ignored for layout) The component recalculates total height from `data`.
 * @param {string} [color] - Bar color (fallbacks to `SUCCESS_GREEN` if not provided).
 * @param {Function} numberFormatter - Formatter for numeric values when not showing percentages.
 * @param {boolean} [showAsPercentages=false] - If true, converts values to percentages of the total.
 *
 * @returns {JSX.Element} The rendered breakdown chart.
 */

const PerformanceBreakdownPlot = ({ data, width, height, color, numberFormatter, showAsPercentages = false }) => {
  const svgRef = React.useRef();
  const tooltipRef = React.useRef();
  const [tooltipContent, setTooltipContent] = React.useState('');
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });

  const plotData = React.useMemo(() => (showAsPercentages ? toPercentages(data) : data), [data, showAsPercentages]);

  const patternId = React.useRef(
    `hatch-${Math.random()
      .toString(36)
      .slice(2)}`,
  ).current;

  React.useLayoutEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const measureText = (() => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = '500 14px URWDIN, sans-serif';
      return t => ctx.measureText(String(t)).width;
    })();

    const solidColor = color ?? SUCCESS_GREEN;

    // defs: patrón hatch
    const defs = svg.append('defs');
    const pattern = defs
      .append('pattern')
      .attr('id', patternId)
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 3)
      .attr('height', 5)
      .attr('patternTransform', 'rotate(135)');

    pattern
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 4)
      .attr('stroke', solidColor)
      .attr('stroke-width', 2);

    const totalsForLabel = plotData.map(d =>
      showAsPercentages
        ? `${(Number(d.current || 0) + Number(d.forecast || 0)).toFixed(1)}%`
        : numberFormatter(Number(d.current || 0) + Number(d.forecast || 0)),
    );
    const maxLabelWidth = totalsForLabel.length ? Math.max(...totalsForLabel.map(t => measureText(t))) : 0;
    const RIGHT_LABEL_PAD = 12;
    const reservedRight = Math.ceil(maxLabelWidth) + RIGHT_LABEL_PAD;

    const innerWidth = Math.max(0, width - MARGIN.LEFT - MARGIN.RIGHT - reservedRight);

    const ROW_HEIGHT = BAR_HEIGHT + LABEL_GAP + LABEL_LINE_HEIGHT + BAR_SPACING;
    const totalHeight = plotData.length * ROW_HEIGHT + MARGIN.TOP + MARGIN.BOTTOM;
    const innerHeight = totalHeight - MARGIN.TOP - MARGIN.BOTTOM;

    svg.attr('width', width).attr('height', totalHeight);

    const maxX = d3.max(plotData, d => Number(d.current || 0) + Number(d.forecast || 0)) || 0;
    const xScale = d3
      .scaleLinear()
      .domain([0, (showAsPercentages ? maxX : maxX * 1.1) || 1])
      .range([0, innerWidth])
      .nice();

    const yScale = d3
      .scaleBand()
      .domain(plotData.map(d => d.name))
      .range([0, innerHeight])
      .padding(PADDING);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.LEFT},${MARGIN.TOP})`);

    const handleMouseOver = (content, x, y) => {
      setTooltipContent(content);
      setTooltipPosition({ x, y });
    };
    const handleMouseOut = () => setTooltipContent('');

    const rows = g
      .selectAll('g.bar')
      .data(plotData, d => d.name)
      .enter()
      .append('g')
      .attr('class', 'bar')
      .attr('transform', d => `translate(0, ${yScale(d.name)})`);

    rows
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', d => xScale(Number(d.current || 0)))
      .attr('height', BAR_HEIGHT)
      .attr('fill', solidColor)
      .style('shape-rendering', 'crispEdges')
      .on('mouseover', d => {
        const cw = xScale(Number(d.current || 0));
        if (d.currentTooltip && cw > 0) {
          const yMid = yScale(d.name) + BAR_HEIGHT / 2;
          const content = showAsPercentages ? `Actuals: ${Number(d.current || 0)}%` : d.currentTooltip;
          handleMouseOver(content, cw / 2, yMid);
        }
      })
      .on('mouseout', handleMouseOut);

    // tramo forecast (hatch)
    rows
      .append('rect')
      .attr('x', d => xScale(Number(d.current || 0)))
      .attr('y', 0)
      .attr('width', d => {
        const total = Number(d.current || 0) + Number(d.forecast || 0);
        return Math.max(0, xScale(total) - xScale(Number(d.current || 0)));
      })
      .attr('height', BAR_HEIGHT)
      .attr('fill', `url(#${patternId})`)
      .on('mouseover', d => {
        const cw = xScale(Number(d.current || 0));
        const total = Number(d.current || 0) + Number(d.forecast || 0);
        const tw = xScale(total);
        const fw = Math.max(0, tw - cw);
        if (d.forecastTooltip && fw > 0) {
          const yMid = yScale(d.name) + BAR_HEIGHT / 2;
          const content = showAsPercentages ? `Forecast: ${Number(d.forecast || 0)}%` : d.forecastTooltip;
          handleMouseOver(content, cw + fw / 2, yMid);
        }
      })
      .on('mouseout', handleMouseOut);

    rows.each(d => {
      const total = Number(d.current || 0) + Number(d.forecast || 0);
      const totalW = xScale(total);

      const labelText = showAsPercentages ? `${total.toFixed(1)}%` : numberFormatter(total);
      const labelW = measureText(labelText);
      const leftX = Math.min(totalW, innerWidth + reservedRight - labelW);
      const rowY = yScale(d.name) || 0;

      const text = g
        .append('text')
        .attr('x', leftX)
        .attr('dx', '0.3em')
        .attr('y', rowY + BAR_HEIGHT / 2)
        .attr('dy', '0.35em')
        .style('text-anchor', 'start')
        .style('fill', FONT_BLACK)
        .style('font-family', 'URWDIN')
        .style('font-weight', '500')
        .style('font-size', '14px')
        .text(labelText);

      const bbox = text.node().getBBox();
      g.insert('rect', () => text.node())
        .attr('x', bbox.x - 2)
        .attr('y', bbox.y)
        .attr('width', bbox.width + 4)
        .attr('height', bbox.height)
        .attr('fill', 'white');
    });

    rows
      .append('text')
      .attr('x', 0)
      .attr('y', BAR_HEIGHT + LABEL_GAP + LABEL_LINE_HEIGHT / 2)
      .attr('dy', '0.35em')
      .style('text-anchor', 'start')
      .style('fill', FONT_BLACK)
      .style('font-family', 'URWDIN')
      .style('font-weight', '500')
      .style('font-size', `${LABEL_FONT_SIZE}px`)
      .text(d => d.name);
  }, [plotData, numberFormatter, width, height, color, showAsPercentages, patternId]);

  return (
    <div style={{ position: 'relative', overflow: 'visible' }}>
      <svg ref={svgRef} width={width} height={height} />
      {tooltipContent && <Tooltip ref={tooltipRef} {...tooltipPosition} content={tooltipContent} transform />}
    </div>
  );
};

export default PerformanceBreakdownPlot;
